import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { CinchActionButton } from '../buttons/cinch-action-button.component';
import { CloseIcon } from '../../../components/mui/icon';
import { CinchActionButtonText } from '../buttons/cinch-action-button-text.component';

/**
 * A custom MUI Dialog Component that is used to display data.
 *
 *
 * @param {string} title - The title of the dialog.
 *
 * @param {string} bodyText - The body text to place under the divider.
 *
 * @param {object} bodyData - The data to place under the body text. ie a table or list component.
 *
 * @param {string} size - The size of the dialog. Options are "xsm", "sm", "md", "lg", default is H:400px, W:500px.
 *
 * @param {string} customHeight - Custom height for the dialog (e.g., "500px"). Overrides size-based height.
 *
 * @param {string} customWidth - Custom width for the dialog (e.g., "650px"). Overrides size-based width.
 *
 * @param {function} onClose - The action that the cancel button will perform on click.
 *
 * @param {function} onSubmit - The action that the submit button will perform on click.
 *
 * @param {string} submitText - The text that will be displayed in the submit button.
 *
 * @param {function} tertiary - The tertiary action button that will perform on click.
 *
 * @param {string} tertiaryText - The text that will be displayed in the tertiary button.
 *
 * @param {boolean} open - The open condition state of the component.
 *
 * @param {boolean} isLoading - The loading state of the data being loaded. Used for circular progress loader.
 *
 * @param {boolean} isInitialLoading - The initial loading state of the component on render. Used for skeleton loader.
 *
 * */

export const CinchDialog = props => {
  // need to decide on better sizes because warning dialog is inherintly small whereas this dialog will be quite larger for ~75% of screenspace
  //   i also need to leave space for data instead of just the description body text. props.bodyData probably under OPTIONAL desc

  useEffect(() => {
    // toggle off skeleton when done loading
  }, [props.isInitialLoading]);
  return <>
      <Dialog slotProps={{
      paper: {
        sx: {
          borderRadius: '28px',
          height: props.customHeight || (props.size === 'xsm' ? '375px' : props.size === 'sm' ? '435px' : props.size === 'md' ? '700px' : props.size === 'lg' ? '900px' : '400px'),
          width: props.customWidth || (props.size === 'xsm' ? '600px' : props.size === 'sm' ? '620px' : props.size === 'md' ? '800px' : props.size === 'lg' ? '1000px' : '500px'),
          pb: '10px',
          overflow: 'hidden'
        }
      }
    }} open={props.open} onClose={props.onClose} maxWidth={props.customWidth ? false : undefined}>
        <DialogTitle>
          {props.isInitialLoading ? <Skeleton variant="rectangular" width="50%" height={20} /> : props.title || 'Warning'}
          <IconButton aria-label="close" onClick={props.onClose} sx={{
          position: 'absolute',
          right: '10px',
          top: '10px'
        }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{
        pb: '20px'
      }}>
          <Divider />
        </Box>
        <DialogContent sx={{
        p: '0px 24px'
      }}>
          <Stack spacing={2}>
            <Box>
              {props.bodyText && props.isInitialLoading ? <Skeleton variant="rectangular" width="100%" height={20} /> : <Typography>{props.bodyText}</Typography>}
            </Box>
            {props.bodyData && props.isInitialLoading ? <Skeleton variant="rectangular" width="100%" height={200} /> : <Box>{props.isLoading && props.bodyData ? <CircularProgress /> : props.bodyData}</Box>}
          </Stack>
        </DialogContent>
        <Box sx={{
        pt: '20px',
        pb: '20px'
      }}>
          <Divider />
        </Box>
        <DialogActions sx={{
        pb: '10px',
        pr: '24px'
      }}>
          {props.actions ? <Box id={`${props.title}-action-custom`} sx={{
          width: '100%'
        }}>{props.actions}</Box> : <>
              {/* if tertiary is passed, then add a third button on the left and cancel/confirm on the right */}
              <Stack direction="row" justifyContent={props.tertiary ? 'space-between' : null}>
                {/* tertiary */}
                <Box>
                  {props.isInitialLoading && props.tertiary ? <Skeleton variant="rectangular" width="100%" height={50} /> : props.tertiary ? <CinchActionButton text="" onClick={props.tertiaryText} /> : null}
                </Box>
                <Box>
                  <Stack direction="row" spacing={'24px'} sx={{
                pr: '19px'
              }}>
                    <Box>
                      {props.isInitialLoading ? <Skeleton variant="rectangular" width="100%" height={50} /> : <CinchActionButton isCancel={true} text="Cancel" onClick={props.onClose} />}
                    </Box>
                    <Box>
                      {props.isInitialLoading ? <Skeleton variant="rectangular" width="100%" height={50} /> : <CinchActionButton text={props.submitText || 'Confirm'} onClick={props.onSubmit} disabled={props.isLoading || false} afterIcon={props.isLoading ? <CircularProgress size={14} sx={{
                    color: '#F3F3F4'
                  }} /> : null} />}
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </>}
        </DialogActions>
      </Dialog>
    </>;
};
CinchDialog.propTypes = {
  /** The title of the tooltip. */
  title: PropTypes.string,
  /** The body text to place under the divider. */
  bodyText: PropTypes.string,
  /** The data to place under the body text. ie a table or list component. */
  bodyData: PropTypes.object,
  /** The size of the dialog. Options are "sm", "md", "lg", default is H:400px, W:500px. */
  size: PropTypes.oneOf(['xsm', 'sm', 'md', 'lg']),
  /** Custom height for the dialog (e.g., "500px"). Overrides size-based height. */
  customHeight: PropTypes.string,
  /** Custom width for the dialog (e.g., "650px"). Overrides size-based width. */
  customWidth: PropTypes.string,
  /** The action that the cancel button will perform on click. */
  onClose: PropTypes.func,
  /** The action that the confirm button will perform on click on click. */
  onSubmit: PropTypes.func,
  /** The loading state of the data being loaded. Used for circular progress loader. */
  isLoading: PropTypes.bool,
  /** The initial loading state of the component on render. Used for skeleton loader. */
  isInitialLoading: PropTypes.bool,
  /** The tertiary action button that will perform on click. */
  tertiary: PropTypes.func,
  /** The open condition state of the component. */
  open: PropTypes.bool.isRequired
};
CinchDialog.defaultProps = {
  size: 'md'
};